import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '@/features/auth/services/auth.service';

/**
 * FR-ONB-004 — terminal screen for a Government Inspector whose tenant has the
 * `government_inspector_access` flag turned off. The backend blocks every
 * tenant-data request for these users, so rendering the normal shell would only
 * produce a wall of 403s. We show the reason and a way out instead.
 */
export function InspectorAccessDisabled({ tenantName }: { tenantName?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = useCallback(() => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      void authService.logout(token).catch(() => {
        /* best-effort: client-side logout proceeds regardless */
      });
    }
    void queryClient.cancelQueries();
    // No clear() here: logout() already resets the cache, and it keeps the
    // workspace identity. Clearing outright dropped that too, so the sign-in
    // screen we navigate to wore the platform's branding until a full reload.
    logout();
    void navigate('/auth/login', { replace: true });
  }, [logout, navigate, queryClient]);

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border bg-background p-8 text-center shadow-sm">
        <div className="mx-auto mb-5 flex size-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
          <ShieldOff className="size-6" aria-hidden="true" />
        </div>
        <h1 className="text-lg font-semibold">Inspector access is turned off</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {tenantName ? `${tenantName} has` : 'This organisation has'} disabled
          government inspector access, so your account cannot open any records
          right now.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Ask the organisation&apos;s IT Administrator to enable{' '}
          <span className="font-medium text-foreground">
            Government Inspector Access
          </span>{' '}
          under Settings &rarr; Feature Flags, then sign in again.
        </p>
        <Button className="mt-6 w-full" onClick={handleLogout}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
